import { captureClientException } from "@client/lib/sentry";
import { trpc } from "@client/lib/trpc-client";
import { skipToken, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DiscoveryHeader } from "../-components/discovery-header";
import { DiscoveryProgressCard } from "../-components/discovery-progress-card";
import { DiscoveryQuickActions } from "../-components/discovery-quick-actions";
import { DiscoveryStatusHeader } from "../-components/discovery-status-header";

export const Route = createFileRoute("/(protected)/discovery/$discoveryId")({
	component: DiscoveryStatusPage,
});

function DiscoveryStatusPage() {
	const params = Route.useParams();
	const discoveryId = (
		"discoveryId" in params ? params.discoveryId : ""
	) as string;
	const [elapsedTime, setElapsedTime] = useState(0);
	const [previousStatus, setPreviousStatus] = useState<string | null>(null);
	const queryClient = useQueryClient();

	// Stop treating as active after this many seconds in "processing" state
	const DISCOVERY_TIMEOUT_SECONDS = 360; // 6 minutes (task maxDuration is 5 min)

	// Get initial discovery data from our database
	const { data: discoveryStatus, error: discoveryError } =
		trpc.sites.getDiscoveryStatus.useQuery(
			{ discoveryId },
			{
				// Poll for updates while discovery is processing
				refetchInterval: (query) => {
					const data = query.state.data;
					// Stop polling if job appears stuck
					if (elapsedTime > DISCOVERY_TIMEOUT_SECONDS) return false;
					// Poll every 10 seconds if discovery is still processing
					if (data && ["processing", "queued"].includes(data.status)) {
						return 10000;
					}
					return false; // Stop polling when discovery is complete
				},
				refetchIntervalInBackground: false,
			},
		);

	// Get site details if siteId is available
	const { data: siteDetails } = trpc.sites.getById.useQuery(
		discoveryStatus?.siteId ? { id: discoveryStatus.siteId } : skipToken,
	);

	// Report errors to Sentry
	useEffect(() => {
		if (discoveryError) {
			captureClientException(new Error(discoveryError.message), {
				tags: {
					operation: "getDiscoveryStatus",
					component: "DiscoveryStatusPage",
				},
				extra: {
					discoveryId,
					trpcError: discoveryError,
				},
			});
		}
	}, [discoveryError, discoveryId]);

	// Normalize SQLite date strings to proper UTC ISO format
	// SQLite CURRENT_TIMESTAMP stores UTC without 'Z' suffix (e.g., "2026-02-17 19:16:11")
	// JavaScript parses this as local time, causing timezone mismatch
	const parseAsUTC = useCallback(
		(dateStr: string | Date | undefined | null): number | null => {
			if (!dateStr) return null;
			if (dateStr instanceof Date) return dateStr.getTime();
			const str = String(dateStr);
			// If it's already ISO with T and timezone info, parse directly
			if (str.includes("T")) return new Date(str).getTime();
			// SQLite format: "2026-02-17 19:16:11" — append Z to treat as UTC
			return new Date(`${str.replace(" ", "T")}Z`).getTime();
		},
		[],
	);

	// Update elapsed time every second based on actual discovery creation time
	// Stop the timer when discovery reaches a terminal state (completed/failed)
	useEffect(() => {
		const discoveryCreatedAt = parseAsUTC(discoveryStatus?.createdAt);
		if (!discoveryCreatedAt || Number.isNaN(discoveryCreatedAt)) return;

		// Check if discovery is in a terminal state
		const status = discoveryStatus?.status?.toLowerCase();
		const isTerminal = status === "completed" || status === "failed";

		if (isTerminal) {
			// Use completedAt for final elapsed time, or current time as fallback
			const endTime = parseAsUTC(discoveryStatus?.completedAt) || Date.now();
			setElapsedTime(
				Math.max(0, Math.floor((endTime - discoveryCreatedAt) / 1000)),
			);
			return; // Don't start interval — timer is frozen
		}

		const timer = setInterval(() => {
			const now = Date.now();
			setElapsedTime(
				Math.max(0, Math.floor((now - discoveryCreatedAt) / 1000)),
			);
		}, 1000);

		return () => clearInterval(timer);
	}, [
		discoveryStatus?.createdAt,
		discoveryStatus?.completedAt,
		discoveryStatus?.status,
		parseAsUTC,
	]);

	// Handle status changes from discovery data
	const currentStatus = discoveryStatus?.status;
	const currentError = discoveryError?.message;

	// Handle discovery status changes - only show toast when status actually changes
	useEffect(() => {
		if (currentStatus && previousStatus !== null) {
			// Only show toast if status changed from previous state
			if (currentStatus !== previousStatus) {
				if (currentStatus?.toLowerCase() === "completed") {
					toast.success("URL discovery completed successfully!");
					// Refresh credit balance — rescans deduct credits on completion
					queryClient.invalidateQueries({
						queryKey: [["credits", "getBalance"]],
					});
				} else if (currentStatus?.toLowerCase() === "failed") {
					toast.error("URL discovery failed. Please try again.");
				}
			}
		}

		// Update previous status for next comparison
		if (currentStatus) {
			setPreviousStatus(currentStatus);
		}
	}, [currentStatus, previousStatus, queryClient]);

	// Detect stuck jobs: processing for longer than max task duration (5 min) + buffer
	const isStuck =
		discoveryStatus?.status?.toLowerCase() === "processing" &&
		elapsedTime > DISCOVERY_TIMEOUT_SECONDS;

	const getStatusMessage = () => {
		if (currentError) {
			// More specific error messages
			if (currentError.includes("NOT_FOUND")) {
				return "Discovery job not found or expired";
			}
			if (currentError.includes("FORBIDDEN")) {
				return "Access denied to this discovery job";
			}
			return `Error: ${currentError}`;
		}
		if (!discoveryStatus) return "Connecting to discovery service...";

		const status = discoveryStatus?.status?.toLowerCase();

		if (isStuck) {
			return "Discovery is taking longer than expected. It may have failed silently — please try again.";
		}

		switch (status) {
			case "completed":
				return "URL discovery completed successfully!";
			case "failed":
				return discoveryStatus?.error || "URL discovery failed";
			case "processing":
				return "Discovering URLs from your website...";
			case "queued":
				return "Discovery job queued, waiting to start...";
			default:
				return "Processing your request...";
		}
	};

	const getProgressValue = () => {
		if (!discoveryStatus) return 0;

		const status = discoveryStatus?.status?.toLowerCase();

		switch (status) {
			case "completed":
				return 100;
			case "failed":
				return 0;
			case "processing": {
				// Estimate progress based on elapsed time for discovery (usually faster than generation)
				// Calculate elapsed time directly if elapsedTime is 0 (stuck job case)
				let timeBasedElapsed = elapsedTime;
				if (timeBasedElapsed === 0 && discoveryStatus?.createdAt) {
					const discoveryCreatedAt = parseAsUTC(discoveryStatus.createdAt);
					const now = Date.now();
					timeBasedElapsed = discoveryCreatedAt
						? Math.max(0, Math.floor((now - discoveryCreatedAt) / 1000))
						: 0;
				}

				const estimatedDuration = 60; // 1 minute average for discovery
				const progress = Math.min(
					(timeBasedElapsed / estimatedDuration) * 90,
					90,
				); // Max 90% until completed
				return progress;
			}
			case "queued":
				return 5;
			default:
				return 10;
		}
	};

	return (
		<div className="container mx-auto max-w-4xl px-4 py-6">
			{/* Header with navigation */}
			<DiscoveryHeader discoveryId={discoveryId} siteDetails={siteDetails} />

			{/* Header Section */}
			<DiscoveryStatusHeader
				status={currentStatus}
				error={currentError}
				siteDetails={siteDetails}
				statusMessage={getStatusMessage()}
			/>

			{/* Main Progress Card */}
			<DiscoveryProgressCard
				progressValue={getProgressValue()}
				status={isStuck ? "failed" : currentStatus}
				error={
					isStuck
						? "Discovery timed out. The background job may have failed to report back."
						: currentError
				}
				discoveryError={discoveryStatus?.error ?? undefined}
				discoveredUrls={discoveryStatus?.discoveredUrls || []}
				totalUrlsFound={discoveryStatus?.totalUrlsFound || 0}
				discoveryType={discoveryStatus?.discoveryType}
				creditsCost={discoveryStatus?.creditsCost || 0}
				siteId={discoveryStatus?.siteId ?? undefined}
				elapsedTime={elapsedTime}
			/>

			{/* Quick Actions */}
			<DiscoveryQuickActions
				siteId={discoveryStatus?.siteId ?? undefined}
				discoveryId={discoveryId}
				discoveryStatus={currentStatus}
			/>

			{/* Debug info in development */}
			{process.env.NODE_ENV === "development" && (
				<div className="mt-4 rounded p-4 text-xs dark:bg-gray-800">
					<p>
						<strong>Debug Info:</strong>
					</p>
					<p>Discovery Status: {discoveryStatus?.status}</p>
					<p>Trigger Run ID: {discoveryStatus?.triggerRunId}</p>
					<p>Discovery Error: {discoveryError?.message}</p>
					<p>Elapsed Time: {elapsedTime}s</p>
				</div>
			)}
		</div>
	);
}
